/**
 * useChannelList — v.3 wiring port of
 * `frontend-v2/src/hooks/useChannelList.ts` (v.1-D §7.12, Decision D-007).
 *
 * Merges two reactive Convex subscriptions into the single denormalised
 * row list the channel grid renders:
 *
 *   - `api.channels.listChannels` — static channel rows.
 *   - `api.channelState.getRunningChannelsSnapshot` — per-running-channel
 *     `ChannelState` (`running` | `ad_break`). ONE batch query, NOT N
 *     per-card fan-outs (NFR-004 — the v.1-era N+1 pattern that scaled
 *     O(channels)).
 *
 * Loading: returns `undefined` until BOTH subscriptions have resolved.
 * D-007: a half-merged view would flicker channels between "idle" and
 * "live" on every snapshot tick.
 *
 * Honest-state defaulting: a channel that is in `listChannels` but NOT in
 * the running snapshot defaults to `backendStatus: 'idle'` — never invents
 * "running" from absence (binding_shapes.md §1 explicitly: "the adapter
 * must NOT infer 'channel is running' from absence of a state row").
 *
 * v.3 port-site fix #2 (T106 §2.3 → T308 / plan §c'):
 *   The plan's "extend the v2 merge from `channels ⋈ channelState` (2
 *   joins) to include `⋈ sloDaily ⋈ uptimeMetrics` (4 joins per FR-006)"
 *   guidance applies to the **per-channel CARD** detail view (FR-006:
 *   "channel detail view / When the card hydrates / Then ... uptime from
 *   the SLO read"), NOT the channel-list grid. NFR-004 + the T307
 *   structural test pin the LIST to exactly ONE batch snapshot. The
 *   per-channel SLO/uptime composition belongs in `useChannelCard` and is
 *   wired there. This file stays at 2 useQuery calls regardless of fleet
 *   size — the strict structural assertion in the T307 list-test is the
 *   load-bearing anti-regression for that invariant.
 */
import { useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { ChannelStatus } from '../lib/adapters';

/**
 * Backend `channelState` literal. Mirrors `ChannelStatus` from the adapter
 * keys; the runtime values are the six closed strings in
 * `tests/fixtures/enums.snapshot.ts` (verified by the T306 enum guardrail).
 */
type ChannelState = ChannelStatus;

export interface ChannelListRow {
  /** Convex row id — also the parent grid's React key + per-card error boundary scope. */
  _id: string;
  /** Operator-facing channel name (from the `channels` row, verbatim). */
  name: string;
  /** Optional human-friendly slug. */
  slug?: string | undefined;
  /** Configured edge provider (e.g. `'aws'`, `'gcore'`). */
  edgeProvider?: string;
  /** Where the channel publishes (YouTube / O&O / both). */
  outputDestination?: 'youtube_live' | 'o_and_o' | 'both' | undefined;
  /**
   * Backend `channelState` overlay. `'idle'` when the channel is not in
   * the running snapshot (honest default — never invent "running" from
   * absence). `'running'` / `'ad_break'` when the snapshot includes it.
   */
  backendStatus: ChannelState;
}

interface ChannelRow {
  _id: string;
  name: string;
  slug?: string | undefined;
  edgeProvider?: string;
  outputDestination?: 'youtube_live' | 'o_and_o' | 'both' | undefined;
}

interface RunningSnapshotEntry {
  channelId: string;
  status: ChannelState;
}

export function useChannelList(): ChannelListRow[] | undefined {
  const channels = useQuery(api.channels.listChannels) as
    | ChannelRow[]
    | undefined;
  const liveStates = useQuery(api.channelState.getRunningChannelsSnapshot) as
    | RunningSnapshotEntry[]
    | undefined;

  const statusByChannelId = useMemo<Map<string, ChannelState> | null>(() => {
    if (liveStates === undefined) return null;
    const m = new Map<string, ChannelState>();
    for (const s of liveStates) m.set(s.channelId, s.status);
    return m;
  }, [liveStates]);

  return useMemo(() => {
    // D-007: surface undefined until BOTH subscriptions have resolved, so
    // we never paint a flickering half-merged view.
    if (channels === undefined || statusByChannelId === null) return undefined;
    return channels.map<ChannelListRow>((c) => {
      // binding_shapes.md §1: absence in the snapshot MUST default to
      // 'idle', never to 'running'.
      const backendStatus: ChannelState =
        statusByChannelId.get(c._id) ?? 'idle';
      return {
        _id:               c._id,
        name:              c.name,
        slug:              c.slug,
        edgeProvider:      c.edgeProvider,
        outputDestination: c.outputDestination,
        backendStatus,
      };
    });
  }, [channels, statusByChannelId]);
}
